import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createSeedState, STORAGE_KEY, STATE_VERSION, DEMO_DAY } from "./seed";
import { nowStamp, randomToken } from "./utils";
import { clearStoredCvs } from "./cv";
import { isInside } from "./analytics";

const DemoContext = createContext(null);
const NAV_KEY = "upv_demo_nav_hidden";

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const s = JSON.parse(raw);
      if (s.version === STATE_VERSION) return { ...s, companyApplications: s.companyApplications || createSeedState().companyApplications };
    }
  } catch (e) {
    /* corrupted state falls back to seed */
  }
  return createSeedState();
}

let seq = 0;
const liveId = (prefix) => `live-${prefix}-${Date.now()}-${seq++}`;
const byToken = (s, token) => s.participants.find((p) => p.token === token);

export const DemoProvider = ({ children }) => {
  const [state, setState] = useState(loadState);
  const ref = useRef(state);
  const [navHidden, setNavHiddenState] = useState(() => localStorage.getItem(NAV_KEY) === "1");

  const commit = useCallback((next) => {
    ref.current = next;
    setState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      /* storage full: keep in memory */
    }
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        const s = loadState();
        ref.current = s;
        setState(s);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const actions = useMemo(() => ({
    register(data) {
      const s = ref.current;
      const existing = s.participants.find((p) => p.email.toLowerCase() === data.email.trim().toLowerCase());
      let participant;
      let participants;
      if (existing) {
        participant = { ...existing, ...data, email: data.email.trim(), registeredAt: Date.now() };
        participants = s.participants.map((p) => (p.id === existing.id ? participant : p));
      } else {
        participant = { id: `p-${Date.now()}`, token: randomToken(), ...data, email: data.email.trim(), registeredAt: Date.now() };
        participants = [...s.participants, participant];
      }
      commit({ ...s, participants, activeParticipantId: participant.id });
      return participant;
    },
    recordAccess(token, checkpointId, type) {
      const s = ref.current;
      const participant = byToken(s, token);
      if (!participant) return { status: "invalid" };
      const inside = isInside(s, participant.id);
      if (type === "entry" && inside) return { status: "already_inside", participant };
      if (type === "exit" && !inside) return { status: "not_inside", participant };
      const event = { id: liveId("a"), participantId: participant.id, checkpointId, type, ...nowStamp(DEMO_DAY) };
      commit({ ...s, accessEvents: [...s.accessEvents, event] });
      return { status: "ok", participant, event, type };
    },
    recordCompanyScan(companyId, token, deviceId, method = "QR") {
      const s = ref.current;
      const participant = byToken(s, token);
      if (!participant) return { status: "invalid" };
      const existing = s.interactions.find((i) => i.companyId === companyId && i.participantId === participant.id);
      if (existing) return { status: "duplicate", participant, interaction: existing };
      const interaction = { id: liveId("i"), participantId: participant.id, companyId, deviceId, method, ...nowStamp(DEMO_DAY) };
      commit({ ...s, interactions: [...s.interactions, interaction] });
      return { status: "received", participant, interaction };
    },
    setActiveParticipant(id) {
      commit({ ...ref.current, activeParticipantId: id });
    },
    submitApplication(data) {
      const s = ref.current;
      const app = { id: `app-${Date.now()}`, ref: `FE27-${101 + s.companyApplications.length}`, status: "pending_payment", createdAt: Date.now(), receipt: null, ...data };
      commit({ ...s, companyApplications: [...s.companyApplications, app] });
      return app;
    },
    updateApplication(id, patch) {
      const s = ref.current;
      commit({ ...s, companyApplications: s.companyApplications.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
    },
    resetDemo() {
      clearStoredCvs();
      commit(createSeedState());
    },
  }), [commit]);

  const setNavHidden = useCallback((v) => {
    setNavHiddenState(v);
    localStorage.setItem(NAV_KEY, v ? "1" : "0");
  }, []);

  const value = useMemo(() => {
    const participantsById = Object.fromEntries(state.participants.map((p) => [p.id, p]));
    return {
      state,
      ...actions,
      participantsById,
      activeParticipant: participantsById[state.activeParticipantId] || state.participants[0],
      navHidden,
      setNavHidden,
    };
  }, [state, actions, navHidden, setNavHidden]);

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
};

export const useDemo = () => useContext(DemoContext);
